/**
 * Local-day timeline combining tasks, ghost suggestions, work segments, and timed external
 * commitments. Uses bounded range caching during background calendar refresh and task snapshots to
 * avoid stale replacement. Supports density/pinch zoom, duration-preserving drag moves, free-time
 * commands, and task/commitment editors.
 *
 * @module apps/web/src/components/calendar/DayView
 */

import { activityIsDone } from '@/components/activities/ActivityDone';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useAfterPaint } from '@/hooks/useAfterPaint';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { addDays, format, startOfWeek } from 'date-fns';
import type { GhostBlocksData, CalendarEventWire, ScheduleBlock, TaskUI } from '@fluxability/types';
import { numberToPriority, taskToUI } from '@fluxability/types';
import { useScheduleStore } from '@/stores/scheduleStore';
import type { CalendarEvent } from '@/stores/scheduleStore';
import { useRouteSchedule, useRouteTasks } from '@/stores/routeData';
import { useTasksStore, captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import {
  CalendarTimelineCell,
  EMPTY_TIMELINE_ENTRIES,
  type TimelineEntry,
} from './CalendarTimeline';
import { assignLanes, laneInterval } from './timelineLanes';
import { CalendarCommitmentEditor } from './CalendarCommitmentEditor';
import { CalendarEmptyState } from './CalendarEmptyState';
import { CalendarChrome, CalendarCaption } from './CalendarChrome';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Detached, useDetachedState } from '@/hooks/useDetachedState';
import { api } from '@/lib/api';
import { afterNextPaint } from '@/lib/afterPaint';
import { rolloverCalendarLocally } from '@/lib/calendarRollover';
import { captureCalendarReadFence } from '@/lib/browserGhostBlocks';
import { getCachedCalendarEvents, setCachedCalendarEvents } from '@/lib/calendarCache';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { CalendarClock } from 'lucide-react';
import { formatDateHeader } from '@/lib/utils';

/** Local-clock hour labels for the 24-row day grid; each visible date supplies its own calendar context. */
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/** Format calendar-cell identity from local year/month/day fields so UTC conversion cannot shift the displayed date. */
function toLocalDateString(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

/** Find Monday at the start of the local week used by schedule range requests. */
function asWeekStart(date: Date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/** Choose this view’s base hour-cell height from the user’s density preference; gesture zoom applies separately. */
function densitySlotHeight(density: 'compact' | 'balanced' | 'spacious') {
  if (density === 'compact') return 56;
  if (density === 'spacious') return 96;
  return 72;
}

/** Translate the dragged block’s center within the target hour into a clamped minute, using the supplied fallback when drag geometry is unavailable. Server scheduling still validates the proposed time. */
function getDropMinute(event: DragEndEvent, fallbackMinute: number, slotHeight: number) {
  const translated = event.active.rect.current.translated;
  const initial = event.active.rect.current.initial;
  const activeRect = translated || initial;
  const overRect = event.over?.rect;
  if (!activeRect || !overRect) return fallbackMinute;

  const pointerY = activeRect.top + activeRect.height / 2;
  const relative = Math.max(0, Math.min(slotHeight, pointerY - overRect.top));
  return Math.max(0, Math.min(59, Math.round((relative / slotHeight) * 60)));
}

/**
 * Local-day timeline combining tasks, ghost suggestions, work segments, and timed external commitments. Uses bounded range caching during background calendar refresh and task snapshots to avoid stale replacement. Supports density/pinch zoom, duration-preserving drag moves, free-time commands, and task/commitment editors.
 */
export function DayView() {
  const { toast } = useToast();
  const [depth, setDepth] = useState(true);
  const timelineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (timelineRef.current) timelineRef.current.scrollTop = 8 * 72;
  }, []);
  const [slotHeight, setSlotHeight] = useState(72);
  // A zoom step resizes the grid rows at once (one style on the grid, not one per cell); blocks switch between
  // compact and full layouts in the render after that frame's paint.
  const blockSlotHeight = useAfterPaint(slotHeight);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFreeingTime, setIsFreeingTime] = useState(false);
  // Each editor owns the record it shows, so opening one re-renders that editor, not the grid.
  const commitmentEditor = useDetachedState<CalendarEvent | null>();
  const setEditingCalendarEvent = commitmentEditor.set;
  const taskEditor = useDetachedState<TaskUI | null>();
  const setEditingTask = taskEditor.set;

  const currentDate = useScheduleStore(s => s.currentDate);
  const setCurrentDate = useScheduleStore(s => s.setCurrentDate);
  const setView = useScheduleStore(s => s.setView);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  // Read through route context so a hidden (kept-alive) Day stays current in the background.
  const { blocks, calendarEvents } = useRouteSchedule();
  const tasks = useRouteTasks();
  const setTasks = useTasksStore(s => s.setTasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTask = useTasksStore(s => s.deleteTask);
  const workHours = useUserStore(s => s.settings.workHours);
  const calendarDensity = useUserStore(s => s.settings.calendarDensity);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 10 },
    })
  );

  const dateKey = toLocalDateString(currentDate);
  const weekStartKey = toLocalDateString(asWeekStart(currentDate));
  const workingWindow = useMemo(() => parseWorkingWindow(workHours), [workHours]);

  useEffect(() => {
    setView('day');
  }, [setView]);

  useEffect(() => {
    setSlotHeight(densitySlotHeight(calendarDensity));
  }, [calendarDensity]);

  // refreshDay reads the latest state; only a change of visible day triggers it here.
  const refreshVisibleDay = useEffectEvent(() => {
    void refreshDay();
  });
  // Refresh after the screen has painted, so a mount or a keep-alive reveal shows at once.
  useEffect(() => afterNextPaint(() => refreshVisibleDay()), [dateKey]);

  const scheduledTaskBlocks = useMemo<ScheduleBlock[]>(() => {
    const recorded = new Set(blocks.filter(block => block.sessionId).map(block => block.taskId));
    return tasks
      .filter(t => t.scheduledStart && t.scheduledEnd && !(activityIsDone(t) && recorded.has(t.id)))
      .map(t => ({
        id: `task-${t.id}`,
        taskId: t.id,
        title: t.title,
        startTime: t.scheduledStart!,
        endTime: t.scheduledEnd!,
        isGhost: false,
        priority: t.priority,
        requiredEnergy: t.energyLevel,
        stateProfile: t.stateProfile,
        customAttributes: t.customAttributes,
      }));
  }, [tasks, blocks]);

  const calendarBlocks = useMemo<ScheduleBlock[]>(() => {
    return calendarEvents.map(event => ({
      id: `gcal-${event.id}`,
      taskId: event.id,
      title: event.title,
      startTime: event.start,
      endTime: event.end,
      isGhost: false,
      isCalendarEvent: true,
      isFreeTimeHold: event.isFlexHold,
      allowsConflict: event.allowConflict,
      priority: event.isFlexHold ? 5 : 4,
      stateProfile: event.stateProfile,
      customAttributes: event.customAttributes,
      requiredEnergy: event.stateProfile?.required_productivity_energy ?? undefined,
    }));
  }, [calendarEvents]);

  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const calendarEventById = useMemo(
    () => new Map(calendarEvents.map(event => [event.id, event])),
    [calendarEvents]
  );

  const blocksByHour = useMemo(() => {
    const merged = new Map<string, ScheduleBlock>();
    const taskIds = new Set(tasks.map(task => task.id));

    [...blocks, ...scheduledTaskBlocks, ...calendarBlocks].forEach(block => {
      if (block.isGhost && block.taskId && !taskIds.has(block.taskId)) return;
      if (toLocalDateString(new Date(block.startTime)) !== dateKey) return;
      merged.set(block.id, block);
    });

    const grouped = new Map<number, TimelineEntry[]>();
    // Overlapping blocks (for example concurrent activities) share the column in lanes.
    const placements = assignLanes(Array.from(merged.values(), laneInterval));
    for (const block of merged.values()) {
      const hour = new Date(block.startTime).getHours();
      const existing = grouped.get(hour);
      const entry = { block, task: taskById.get(block.taskId), ...placements.get(block.id) };
      if (existing) existing.push(entry);
      else grouped.set(hour, [entry]);
    }
    return grouped;
  }, [blocks, scheduledTaskBlocks, calendarBlocks, tasks, dateKey, taskById]);
  const hasCalendarContent = useMemo(
    () =>
      Array.from(blocksByHour.values()).some(hourBlocks => hourBlocks.length > 0) || isRefreshing,
    [blocksByHour, isRefreshing]
  );
  const pinchHandlers = usePinchZoom({
    setValue: setSlotHeight,
    min: 40,
    max: 168,
  });

  async function refreshDay() {
    const currentCalendar = captureCalendarReadFence();
    setIsRefreshing(true);
    try {
      await rolloverCalendarLocally(weekStartKey).catch(error =>
        console.warn('Local rollover needs a retry', error)
      );
      if (!currentCalendar()) return;

      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = addDays(dayStart, 1);

      const calendarStart = dayStart.toISOString();
      const calendarEnd = dayEnd.toISOString();
      const cachedCalendarEvents = getCachedCalendarEvents(calendarStart, calendarEnd);
      if (cachedCalendarEvents) setCalendarEvents(cachedCalendarEvents);

      const calendarPromise = api.calendar
        .getEvents(calendarStart, calendarEnd)
        .then(calendarResponse => {
          if (currentCalendar() && calendarResponse.success && calendarResponse.data) {
            const normalizedEvents = normalizeCalendarEvents(calendarResponse.data);
            setCalendarEvents(normalizedEvents);
            setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
          }
        })
        .catch(error => {
          console.warn('Background Google Calendar refresh failed', error);
        });

      const snapshot = captureTaskSnapshot();
      const [tasksResponse, scheduleResponse] = await Promise.all([
        api.tasks.list(),
        api.schedule.getGhostBlocks(weekStartKey),
      ]);

      if (!currentCalendar() || !isTaskSnapshotCurrent(snapshot)) return;
      let freshTaskIds = new Set(tasks.map(task => task.id));
      if (tasksResponse.success && tasksResponse.data) {
        const tasksList = Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : tasksResponse.data.tasks;
        if (tasksList) {
          const nextTasks = tasksList.map(taskToUI);
          setTasks(nextTasks);
          freshTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
        }
      }

      if (scheduleResponse.success && scheduleResponse.data) {
        setBlocks(normalizeScheduleBlocks(scheduleResponse.data, freshTaskIds));
        setGhostBlocks(normalizeGhostBlocks(scheduleResponse.data, freshTaskIds));
        if (!cachedCalendarEvents && scheduleResponse.data.gcal_events?.length) {
          const normalizedEvents = normalizeCalendarEvents(scheduleResponse.data.gcal_events || []);
          setCalendarEvents(normalizedEvents);
          setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
        }
      }

      void calendarPromise;
    } finally {
      setIsRefreshing(false);
    }
  }

  const goToPreviousDay = () => setCurrentDate(addDays(currentDate, -1));
  const goToNextDay = () => setCurrentDate(addDays(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.data.current?.type === 'ScheduledTask' && over.data.current?.type === 'TimeSlot') {
      const block = active.data.current.block as ScheduleBlock;
      if (block.isGhost || block.isCalendarEvent || block.isFreeTimeHold) return;

      const targetDateKey = over.data.current.dateKey;
      const targetHour = over.data.current.hour;
      const oldStart = new Date(block.startTime);
      const durationMs = new Date(block.endTime).getTime() - oldStart.getTime();
      const targetMinute = getDropMinute(event, oldStart.getMinutes(), slotHeight);

      const newStart = new Date(targetDateKey + 'T00:00:00');
      newStart.setHours(targetHour, targetMinute, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);

      updateTask(block.taskId, {
        scheduledStart: newStart.toISOString(),
        scheduledEnd: newEnd.toISOString(),
      });

      try {
        await api.tasks.update(block.taskId, {
          do_date: newStart.toISOString(),
          do_date_end: newEnd.toISOString(),
        });
      } catch (err) {
        console.error('Failed to reschedule activity', err);
        toast({
          title: 'Move failed',
          description: 'The activity could not be moved.',
          variant: 'destructive',
        });
        void refreshDay();
      }
    }
  };

  async function freeUpLater() {
    const time = window.prompt('Start time to protect?', '4pm');
    if (!time) return;
    const duration = window.prompt('How much time?', '60 minutes') || '60 minutes';
    setIsFreeingTime(true);
    try {
      const response = await api.nlp.command(`free up ${duration} at ${time} today`);
      if (!response.success) throw new Error(response.error?.message || 'Could not free time');
      toast({
        title: response.data?.result?.notification?.title || 'Time protected',
        description: response.data?.result?.notification?.message || 'Your schedule was adjusted.',
      });
      await refreshDay();
    } catch (error) {
      toast({
        title: 'Could not free time',
        description: error instanceof Error ? error.message : 'Try a different window.',
        variant: 'destructive',
      });
    } finally {
      setIsFreeingTime(false);
    }
  }

  function handleCalendarEventSaved(updatedEvent: CalendarEvent) {
    setCalendarEvents(
      calendarEvents.map(event => (event.id === updatedEvent.id ? updatedEvent : event))
    );
    setEditingCalendarEvent(null);
    toast({ title: updatedEvent.isFlexHold ? 'Protected time updated' : 'Commitment updated' });
    void refreshDay();
  }

  async function handleSaveTask(updates: Partial<TaskUI>, editingTask: TaskUI | null) {
    if (!editingTask) return;

    try {
      const response = await api.tasks.update(editingTask.id, {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        split_policy: updates.splitPolicy,
        custom_attributes: updates.customAttributes,
      });
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI(response.data?.task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (error) {
      console.error('Failed to save activity', error);
      updateTask(editingTask.id, updates);
    }
  }

  async function handleDeleteTask(editingTask: TaskUI | null) {
    if (!editingTask) return;
    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to delete activity', error);
    }
  }

  async function handleRescheduleTomorrow(editingTask: TaskUI | null) {
    if (!editingTask) return;
    const response = await api.schedule.scheduleTaskTomorrow(editingTask.id);
    if (!response.success) {
      toast({
        title: 'Could not move activity',
        description: response.error?.message || 'No optimal slot was found tomorrow.',
        variant: 'destructive',
      });
      return;
    }
    if (response.data?.task) updateTask(editingTask.id, taskToUI(response.data.task));
    setEditingTask(null);
    await refreshDay();
    toast({
      title: 'Moved to tomorrow',
      description:
        response.data?.notification?.message || 'The scheduler picked the best available slot.',
    });
  }

  const selectRefs = useRef({ taskById, calendarEventById });
  selectRefs.current = { taskById, calendarEventById };
  const selectBlock = useCallback((block: ScheduleBlock) => {
    const current = selectRefs.current;
    if (block.isCalendarEvent) {
      const event = current.calendarEventById.get(block.taskId);
      if (event) setEditingCalendarEvent(event);
    } else if (!block.isGhost) {
      const task = current.taskById.get(block.taskId);
      if (task) setEditingTask(task);
    }
  }, [setEditingCalendarEvent, setEditingTask]);

  return (
    <div className="activity-calendar" data-depth={depth}>
      <CalendarChrome
        view="Day"
        period={formatDateHeader(currentDate)}
        previous={goToPreviousDay}
        next={goToNextDay}
        today={goToToday}
        depth={depth}
        setDepth={setDepth}
        refresh={() => void refreshDay()}
        refreshing={isRefreshing}
        zoom={{
          value: Math.round((slotHeight / 72) * 100),
          out: () => setSlotHeight(value => Math.max(40, value - 12)),
          in: () => setSlotHeight(value => Math.min(156, value + 12)),
        }}
        actions={
          <Button onClick={freeUpLater} disabled={isFreeingTime}>
            <CalendarClock className="mr-2" />
            Free Up
          </Button>
        }
      >
        <span>
          <strong>
            {tasks.filter(task => !['done', 'completed', 'cancelled'].includes(task.status)).length}
          </strong>{' '}
          open activities
        </span>
        <span>{format(currentDate, 'EEEE, MMMM d')}</span>
      </CalendarChrome>
      {!hasCalendarContent && <CalendarEmptyState />}
      <CalendarCaption />

      <div
        ref={timelineRef}
        className="native-scroll temporal-grid calendar-timeline relative"
        {...pinchHandlers}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="w-full pb-8">
            <div className="calendar-day-grid calendar-grid-header sticky top-0 z-10 grid border-b py-3">
              <div />
              <div className="text-muted-foreground text-sm font-medium">
                {format(currentDate, 'EEEE, MMMM d')}
              </div>
            </div>
            {HOURS.map(hour => {
              const hourBlocks = blocksByHour.get(hour) || EMPTY_TIMELINE_ENTRIES;
              const boundaryMinutes = getWorkingBoundaryMinutes(hour, workingWindow);

              return (
                <div
                  key={hour}
                  className="calendar-day-grid grid"
                  style={{ gridAutoRows: `${slotHeight}px` }}
                >
                  <div
                    className={[
                      'calendar-time-label text-right',
                      boundaryMinutes.length > 0 ? 'border-t-foreground/80 border-t-2' : '',
                    ].join(' ')}
                  >
                    {hour === 0
                      ? '12 AM'
                      : hour < 12
                        ? `${hour} AM`
                        : hour === 12
                          ? '12 PM'
                          : `${hour - 12} PM`}
                  </div>

                  {/* The physical hour cell stays registered; dateKey updates its drop data. */}
                  <CalendarTimelineCell
                    id={`day-hour-${hour}`}
                    dateKey={dateKey}
                    hour={hour}
                    slotHeight={blockSlotHeight}
                    workStart={workingWindow.startMinutes}
                    workEnd={workingWindow.endMinutes}
                    entries={hourBlocks}
                    onSelect={selectBlock}
                  />
                </div>
              );
            })}
          </div>
        </DndContext>
      </div>

      <Detached state={commitmentEditor} initial={null}>
        {editingCalendarEvent => (
          <CalendarCommitmentEditor
            event={editingCalendarEvent}
            open={Boolean(editingCalendarEvent)}
            onClose={() => setEditingCalendarEvent(null)}
            onSave={handleCalendarEventSaved}
          />
        )}
      </Detached>
      <Detached state={taskEditor} initial={null}>
        {editingTask => (
          <TaskEditor
            task={editingTask}
            open={Boolean(editingTask)}
            onClose={() => setEditingTask(null)}
            onSave={updates => handleSaveTask(updates, editingTask)}
            onDelete={() => handleDeleteTask(editingTask)}
            onRescheduleTomorrow={() => handleRescheduleTomorrow(editingTask)}
          />
        )}
      </Detached>
    </div>
  );
}

/** Local clock bounds in minutes from midnight used only for work-hour shading and boundary marks. */
type WorkingWindow = {
  /** Local minutes after midnight at which the working interval starts. */
  startMinutes: number;
  /** Local minutes after midnight at which the working interval ends. */
  endMinutes: number;
};

/** Convert preferred start/end clock strings to minute bounds, defaulting absent or nonnumeric values to 09:00–17:00. */
function parseWorkingWindow(
  workHours?: {
    /** Local workday start clock string parsed into minutes after midnight. */ start?: string;
    /** Local workday end clock string parsed into minutes after midnight. */ end?: string;
  } | null
): WorkingWindow {
  return {
    startMinutes: parseClockMinutes(workHours?.start, 9 * 60),
    endMinutes: parseClockMinutes(workHours?.end, 17 * 60),
  };
}

/** Convert an hour/minute string to minutes from midnight and clamp to the day bounds. Missing or nonnumeric input uses the caller’s fallback. */
function parseClockMinutes(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const [hourText, minuteText = '0'] = value.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return fallback;
  return Math.max(0, Math.min(24 * 60, hour * 60 + minute));
}

/** Locate work-window boundaries inside this hour and express them as minute offsets for border positioning. */
function getWorkingBoundaryMinutes(hour: number, window: WorkingWindow): number[] {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return [window.startMinutes, window.endMinutes]
    .filter(minutes => minutes >= hourStart && minutes < hourEnd)
    .map(minutes => minutes - hourStart);
}

/** Project ghost proposals and recorded work into display blocks; pause intervals stay empty. A ghost remains tentative; a work segment represents recorded activity rather than a new scheduling request. */
function normalizeScheduleBlocks(
  data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>,
  taskIds?: Set<string>
): ScheduleBlock[] {
  const ghostBlocks = (data.ghost_blocks || [])
    .filter(block => !taskIds || taskIds.has(block.task_id))
    .map(block => ({
      id: block.id,
      taskId: block.task_id,
      title: block.title || 'Suggested activity',
      startTime: block.proposed_start,
      endTime: block.proposed_end,
      isGhost: true,
    }));

  const workSegments = (data.work_segments || []).filter(segment => segment.segment_type !== 'pause').map(segment => ({
    id: segment.id,
    sessionId: segment.session_id,
    workSegmentIndex: segment.segment_index,
    workSegmentCount: segment.segment_count,
    taskId: segment.task_id ?? '',
    title: segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: 3,
  }));

  return [...ghostBlocks, ...workSegments];
}

/** Project proposed start/end times and confidence/cost explanations into the tentative-block store without confirming the proposed placement. */
function normalizeGhostBlocks(
  data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>,
  taskIds?: Set<string>
) {
  return (data.ghost_blocks || [])
    .filter(block => !taskIds || taskIds.has(block.task_id))
    .map(block => ({
      id: block.id,
      taskId: block.task_id,
      proposedStart: block.proposed_start,
      proposedEnd: block.proposed_end,
      cost: block.cost,
      confidence: block.confidence,
      reason: block.reason,
    }));
}

/** Adapt calendar events into UI records. Accepts both wire spellings: camelCase from `/calendar/events` and snake_case commitments from schedule responses. Defaults commitments to immovable, parses custom attributes, and removes entries that should not appear as timed commitments. */
function normalizeCalendarEvents(events: CalendarEventWire[] = []): CalendarEvent[] {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time || '',
      end: event.end || event.end_time || '',
      isImmovable: Boolean(event.isImmovable ?? event.is_immovable ?? true),
      isFlexHold: Boolean(event.isFlexHold ?? event.is_flex_hold),
      stateProfile: event.stateProfile || event.state_profile || null,
      customAttributes: parseCustomAttributes(event.customAttributes ?? event.custom_attributes),
      allowConflict: Boolean(event.allowConflict ?? event.allow_conflict),
      seriesAllowConflict: Boolean(event.seriesAllowConflict),
      isRecurring: Boolean(event.isRecurring),
    }))
    .filter(isRenderableCalendarEvent);
}

/** Exclude date-only/all-day entries and imported Canvas or Book-Tracker activities from timed commitment rendering. Protected Fluxability holds remain visible after the timed-event check, preventing duplicate imported work on the calendar. */
function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return (
    source !== 'canvas' &&
    source !== 'book-tracker' &&
    !attributes.canvas &&
    !attributes.book_tracker
  );
}

/** Treat either endpoint without a time separator as all-day/untimed. This is a display-shape check, not a full timestamp validator. */
function isAllDayCalendarEvent(event: {
  /** Optional event start text; absence of a time separator marks the event untimed. */ start?: string;
  /** Optional event end text; absence of a time separator marks the event untimed. */ end?: string;
}): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

/** Accept existing object values or JSON object strings for legacy custom attributes. Missing, malformed, or scalar values become an empty record; the function does not validate individual field meanings. */
function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
